import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Image
} from 'antd';
import { connect } from 'react-redux';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import { CS_STYLES } from 'utils/constants/style-constants';
import AiIssueAnalysisAnswerRenderer from 'components/top/AiDrawer/AiIssueAnalysisAnswerRenderer';
import CsSwapSelect from 'components/commons/CsSwapSelect/CsSwapSelect';
import useZuStore from 'store/zustand/zustand-store';
import { AiAnalysisType } from 'utils/constants/issue-ai-status';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';


const markdownComponents = {
  img({ node, ...props}) {
    return <div style={{ maxWidth:'100%' }}>
      <Image src={props.src} />
    </div>
  },
  th: ({ node, ...props }) => (
    <th style={{ border: '1px solid #aaa', padding: '8px', textAlign: 'left' }} {...props}>
      {props.children}
    </th>
  ),
  td: ({ node, ...props }) => (
    <td style={{ border: '1px solid #aaa', padding: '8px' }} {...props}>
      {props.children}
    </td>
  ),
};

function engineLabel(analysisType) {
  if (AiAnalysisType.isCrashClaw(analysisType)) {
    return ze('CrashClaw问题分析', 'CrashClaw Issue Analysis');
  }
  return ze('CrashSight问题分析', 'CrashSight Issue Analysis');
}

const ConversationItem = ({
  conversationId,
  chatHistoryList: unorderedChatHistoryList,
  expanded,
  onChangeExpanded,
  onClickOpenConversation,
  analysisType,
}) => {
  const orderedChatHistoryListLatestFirst = useMemo(() => {
    return (unorderedChatHistoryList || [])
      .sort((a, b) => (b.requestTime || '').localeCompare(a.requestTime || ''))
  }, [unorderedChatHistoryList]);

  const firstChatInfo = orderedChatHistoryListLatestFirst[orderedChatHistoryListLatestFirst.length - 1];
  const lastChatInfo = orderedChatHistoryListLatestFirst[0];

  if (!firstChatInfo || !lastChatInfo) {
    return null;
  }

  const isCrashClaw = AiAnalysisType.isCrashClaw(analysisType);

  const cardHead = <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
    <div>{ lastChatInfo.responseTime }</div>
    <div style={{ marginLeft: '24px', color: CS_STYLES.getPrimaryColor() }}>{ engineLabel(analysisType) }</div>
    <div style={{ marginLeft: '24px' }}>{ ze('创建人：', 'Created By: ')}{ lastChatInfo.userName }</div>
    <div style={{ flexGrow: 1 }}/>
    <div style={{ transform: 'scale(1.5)', display: 'flex', alignItems: 'center', marginRight: '6px' }}><CsCollapseButton
      isCollapsed={!expanded}
      isSmallSize={true}
      onClick={() => onChangeExpanded(!expanded)}
    /></div>
  </div>;

  return <Card
    title={cardHead}
    bodyStyle={{ display: !expanded ? 'none' : undefined, padding: 0 }}
  >
    { expanded && <div style={{ backgroundColor: '#f5f6f8', padding: '16px', position: 'relative' }}>
      { conversationId && <div style={{ position: 'absolute', right: '16px' }}><CsSwapSelect
        type='primary'
        options={[
          { value: true, label: ze('切换对话','Open Chat') },
          { value: false, label: ze('切换对话','Open Chat') },
        ]}
        value={true}
        onChange={v => onClickOpenConversation(conversationId)}
      /></div> }
      { isCrashClaw
        ? <ReactMarkdown
          remarkPlugins={[gfm]}
          linkTarget='_blank'
          children={lastChatInfo.answer}
          components={markdownComponents}
        />
        : <AiIssueAnalysisAnswerRenderer
          text={lastChatInfo.answer}
          enableDeepAnalysis={false}
        /> }
    </div> }
  </Card>;
};

const AiIssueAnalysisHistory = ({
  reduxState,
  appId,
  pid,
  path,
  issueId,
  issue,
  exceptionType,
  exceptionCategory,
}) => {
  const aiDrawerSlice = useZuStore().aiDrawer;
  const issueAnalysisRefreshTrigger = useZuStore(state => state.aiDrawer.issueAnalysisRefreshTrigger);
  const issueAnalysisRefreshIssueId = useZuStore(state => state.aiDrawer.issueAnalysisRefreshIssueId);

  const [conversationList, setConversationList] = useState([]);
  const [conversationIdToExpanded, setConversationIdToExpanded] = useState({});

  useEffect(() => {
    if (appId && issueId) {
      fetchHistory();
    }
  }, [appId, issueId, issueAnalysisRefreshTrigger, issueAnalysisRefreshIssueId]);

  function onClickOpenConversation(conversation) {
    const analysisType = conversation?.analysisType;
    const workspaceUid = conversation?.workspaceUid;
    const sessionId = conversation?.conversationId;
    if (AiAnalysisType.isCrashClaw(analysisType) && workspaceUid && sessionId) {
      aiDrawerSlice.startChatBotWithCrashClaw({
        workspaceUid,
        sessionId,
      });
      return;
    }
    aiDrawerSlice.startChatBotWithIssue(issueId, appId);
  }

  async function fetchHistory() {
    const rsp = await RestHelper.mustPost('/api/aiPilot/getMultipleChatHistory', {
      appId,
      issueId,
    });
    const nextList = rsp.json.data.conversationList;
    setConversationList(nextList);
    const defaultExpandedConversationId = nextList?.[0]?.conversationId;
    setConversationIdToExpanded({ [defaultExpandedConversationId]: true });
  }

  const res = <div>
    { conversationList.map(x => {
      const { data, conversationId, analysisType, workspaceUid } = x;
      return <ConversationItem
        key={conversationId}
        conversationId={conversationId}
        analysisType={analysisType}
        chatHistoryList={data}
        expanded={!!conversationIdToExpanded[conversationId]}
        onChangeExpanded={v => {
          setConversationIdToExpanded(prev => ({
            ...prev,
            [conversationId]: v,
          }));
        }}
        onClickOpenConversation={() => {
          onClickOpenConversation({ conversationId, analysisType, workspaceUid });
        }}
      />;
    }) }
  </div>;

  return res;
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(AiIssueAnalysisHistory);
